import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import actions from 'partners/actions';

import Collection from 'containers/Collection';
import ViewLoading from 'components/view/loading';
import AppIcon from 'components/apps/icon';
import KebabButton from 'components/button/kebab';
import AppStatus from 'admin/components/app/status';
import Link from 'components/link';
import {
  getAppDisplayId,
  MAX_APP_ID_DISPLAY_LEN,
  renderAppTypeLabel,
} from 'utils/app';
import { truncatedText } from 'utils/collection';

import 'partners/components/pages/apps/apps.scss';

const { CLIENT_URL, BASE_URI } = process.env;

/**
 * The row links to the app, so clicks on the kebab trigger must not reach it.
 * Menu items are rendered in a portal and never bubble through the row.
 *
 * @param {React.MouseEvent} event
 * @returns {void}
 */
function stopRowClick(event) {
  event.stopPropagation();
}

const mapStateToProps = (state) => ({
  session: state.session,
  collection: state.data.collection,
});

const mapDispatchToProps = (dispatch) => ({
  linkApps: (apps) => {
    return dispatch(actions.apps.link(apps));
  },

  disconnectApp: (id) => {
    return dispatch(actions.apps.disconnect(id));
  },
});

export class Apps extends React.Component {
  static propTypes = {
    session: pt.object,
    collection: pt.object,

    linkApps: pt.func,
    disconnectApp: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
    notifyError: pt.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  fields = {
    name: {
      columns: [
        {
          id: 'icon',
          label: 'App',
          type: 'image',
          url: '/apps/{id}',
          func(app) {
            return <AppIcon app={app} size={48} />;
          },
        },
        {
          id: 'name',
          type: 'link',
          func: (app) => (
            <Link to={`/apps/${app.id}`} className="collection-table-link">
              {app.name}
            </Link>
          ),
        },
      ],
    },
    id: {
      label: 'App ID',
      func(app) {
        return truncatedText(getAppDisplayId(app), MAX_APP_ID_DISPLAY_LEN);
      },
    },
    type: {
      label: 'Type',
      // No tooltip: its popover requires a `client` context that the partners
      // root does not provide (see `partners/containers/App` childContextTypes).
      func: (app) =>
        app.type ? renderAppTypeLabel(app.type, false) : undefined,
    },
    status: {
      label: 'Status',
      func: (app) => <AppStatus app={app} />,
    },
    client: {
      label: 'Test store',
      path: 'client.name',
    },
    version: {
      label: 'Last version',
      path: 'version',
    },
    installs: {
      label: 'Installs',
      func: (app) => app.installs?.count,
    },
    actions: {
      // Sticks to the right edge while the table scrolls
      className: 'action',
      func: (app) => {
        return (
          <KebabButton
            side="left"
            anchor="left"
            sideOffset={23}
            alignOffset={13}
            onClick={stopRowClick}
            items={[
              <button
                key="0"
                data-id={app.id}
                data-client={app.client_id}
                onClick={this.onClickLogin}
              >
                Log in to development store
              </button>,
              <hr key="1" />,
              <button
                key="2"
                className="danger"
                data-id={app.id}
                disabled={Boolean(app.public_id)}
                title={
                  app.public_id
                    ? 'Public apps can’t be disconnected. Unpublish the app first.'
                    : undefined
                }
                onClick={this.onClickDisconnectApp}
              >
                Disconnect app
              </button>,
            ]}
            button={false}
          />
        );
      },
    },
  };

  getLoginUrl(clientId, to) {
    const { session } = this.props;
    const clientUrl = CLIENT_URL.replace('CLIENT_ID', clientId);
    return `${clientUrl}${BASE_URI}/api/user/login-partner/${session.id}?to=${to}`;
  }

  onClickLogin = (event) => {
    event.preventDefault();

    const { id: appId, client: clientId } = event.currentTarget.dataset;
    // A development app lives in the test environment of its store
    const loginUrl = this.getLoginUrl(clientId, `test/apps/${appId}`);

    window.open(loginUrl, '_blank');
  };

  onClickLinkApps = (event) => {
    event.preventDefault();

    const { openModal } = this.context;

    openModal('LinkApps', {
      onSubmit: this.onLinkApps,
    });
  };

  onClickDisconnectApp = (event) => {
    event.preventDefault();

    const { openModal, notifyError } = this.context;
    const { collection } = this.props;
    const { id: appId } = event.currentTarget.dataset;
    const app = collection?.results?.find((app) => app.id === appId);

    if (!app) {
      return;
    }

    if (app.public_id) {
      notifyError(
        'Public apps can’t be disconnected. Unpublish the app first.',
      );
      return;
    }

    const { name } = app;

    openModal('ConfirmDelete', {
      titleMessage: 'Disconnect app',
      action: 'Disconnect app',
      message: (
        <p>
          Disconnect <b>{name}</b> from this partner dashboard? Stores with this
          app installed will keep it, and existing users keep their access.
        </p>
      ),
      width: 760,
      confirmInput: {
        description: 'To confirm, type the app name below',
        value: name,
      },
      confirmError: `Please enter "${name}" to confirm`,
      onConfirm: () => this.onDisconnectApp(appId),
    });
  };

  onLinkApps = async (apps) => {
    const { linkApps } = this.props;

    this.setState({ loading: true });
    await linkApps(apps);
    this.setState({ loading: false });

    return true;
  };

  onDisconnectApp = async (id) => {
    const { disconnectApp } = this.props;

    this.setState({ loading: true });
    await disconnectApp(id);
    this.setState({ loading: false });

    return true;
  };

  render() {
    const { loading } = this.state;

    if (loading) {
      return <ViewLoading />;
    }

    return (
      <div className="apps-collection">
        <Collection
          title="Apps"
          model="apps"
          endpoint="/partner/apps"
          recordUri="/apps/{id}"
          headerActions={[
            {
              type: 'sub',
              label: 'Connect app',
              onClick: this.onClickLinkApps,
            },
            {
              label: 'New app',
              link: '/apps/new',
            },
          ]}
          emptyTitle="Create a new app"
          emptyDescription="Apps can make schema changes and add custom functionality to any store. Create a new app or connect an existing one to distribute it publicly or privately."
          emptyAction="Create app"
          emptyActionLink="/apps/new"
          emptySecondAction="Connect app"
          onClickSecondAction={this.onClickLinkApps}
          fields={this.fields}
          selectable={false}
          withPreferences={false}
          {...this.props}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Apps);
